import { useSwitcherClient } from "hooks/useSwitcherClient";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import styles from "./index.module.scss";
import { useDispatch } from "react-redux";
import { AppDispatch } from "store/store";
import { CloudRecordingsEmptyState } from "views/page-content/cloud-videos/CloudRecordingsEmptyState";
import { Suspense } from "react";
import { Spinner } from "components/spinners/Spinner";
import { closeCurrentModal, setActiveModal } from "store/modal/slice";
import { Modals } from "store/modal/types";
import { Button } from "components/buttons/Button";
import { SelectFeaturedTrailerModal } from "../modal/SelectFeaturedTrailerModal";
import { DisabledVariant } from "components/entity-details/BroadcastResponseDetails";
import { FeaturedTrailerInput } from "./FeaturedTrailerInput";
import { BroadcastResponse } from "@switcherstudio/api-core-client";

export enum FeaturedVideoType {
    VideoPlayer = "VideoPlayer",
    Catalog = "Catalog"
}

export interface SelectFeaturedTrailerBaseProps {
    selectedBroadcastId: string;
    disabled?: boolean;
    disabledVariant?: DisabledVariant;
    featuredVideoType?: keyof typeof FeaturedVideoType;
    selectedBroadcast?: BroadcastResponse;
    selectedBroadcastLoading?: boolean;
}
export interface SelectFeaturedTrailerProps extends SelectFeaturedTrailerBaseProps {
    onSelectBroadcastId: (broadcastId: string) => void;
    videoPlayerId: string;
}

export const SelectFeaturedTrailer = ({
    selectedBroadcastId,
    onSelectBroadcastId,
    videoPlayerId,
    featuredVideoType = FeaturedVideoType.VideoPlayer,
    disabled = false,
    disabledVariant = featuredVideoType == FeaturedVideoType.Catalog
        ? DisabledVariant.NoInteract
        : DisabledVariant.Disabled,
    selectedBroadcast,
    selectedBroadcastLoading
}: SelectFeaturedTrailerProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    // Get cloud usage data
    const {
        data: cloudUsage,
        dispatchApiRequest: refreshCloudUsage,
        loading
    } = useSwitcherClient((client) => client.cloudRecordings_GetUsage, {
        suppressErrorBoundary: true,
        requestImmediately: true
    });

    // Open/Close modal callbacks
    const closeModal = useCallback(() => {
        dispatch(closeCurrentModal());
    }, [dispatch]);

    const handleOpenModal = useCallback(async () => {
        dispatch(
            setActiveModal({
                id: Modals.AddVideosModal,
                type: Modals.AddVideosModal,
                component: (
                    <SelectFeaturedTrailerModal
                        setIsOpen={closeModal}
                        onSelect={(broadcastIds) => {
                            onSelectBroadcastId(broadcastIds[0]);
                        }}
                        playerId={videoPlayerId}
                        onClose={(broadcastId) => {
                            onSelectBroadcastId(broadcastId);
                            refreshCloudUsage();
                        }}
                        previouslySelectedBroadcastIds={[selectedBroadcastId]}
                    />
                )
            })
        );
    }, [
        closeModal,
        dispatch,
        onSelectBroadcastId,
        refreshCloudUsage,
        selectedBroadcastId,
        videoPlayerId
    ]);

    if (loading) {
        return <Spinner />;
    }

    return (
        <div className={styles["specific-video-container"]}>
            <div className={styles["specific-video-content-container"]}>
                {cloudUsage?.TotalRecordings > 0 ? (
                    <>
                        {selectedBroadcastId && selectedBroadcast && (
                            <FeaturedTrailerInput
                                selectedBroadcast={selectedBroadcast}
                                selectedBroadcastLoading={
                                    selectedBroadcastLoading
                                }
                                selectedBroadcastId={selectedBroadcastId}
                                disabled={disabled}
                                disabledVariant={disabledVariant}
                                featuredVideoType={featuredVideoType}
                            />
                        )}
                        <Button
                            type={
                                featuredVideoType == FeaturedVideoType.Catalog
                                    ? "badge-secondary"
                                    : "box"
                            }
                            onClick={handleOpenModal}
                            disabled={disabled}
                        >
                            {selectedBroadcast
                                ? t("player-status-page:buttons:change-video")
                                : t("player-status-page:buttons:select")}
                        </Button>
                    </>
                ) : (
                    <CloudRecordingsEmptyState
                        uploadVideoButton={
                            <Suspense>
                                <button
                                    className={`btn btn-primary ${styles["upload-button"]}`}
                                    onClick={handleOpenModal}
                                >
                                    {t("video-upload:upload-video")}
                                </button>
                            </Suspense>
                        }
                    />
                )}
            </div>
        </div>
    );
};
